<template>
  <div v-click-outside="onClickOutside" v-bind="$attrs">
    <WdsInput
      :model-value="modelValue"
      :label="label"
      :placeholder="placeholder"
      :error="error"
      :disabled="disabled"
      data-test="creatable-select-input"
      @update:model-value="handleInput"
    >
      <template v-if="$slots.prefix" #prefix>
        <slot name="prefix"></slot>
      </template>
    </WdsInput>
    <div v-show="show" class="creatable-select" tabindex="0">
      <div class="select-wrapper">
        <div class="selected open" :class="{ empty: !dropdownHasContent }">
          <WdsButtonGhost
            v-if="showCreateButton"
            class="create-button"
            lg
            @click="handleClickCreate"
          >
            <WdsStack align-center gap-sm class="tw-w-full">
              <i class="zmdi zmdi-plus" />
              <slot>
                {{ createButtonText }}
              </slot>
            </WdsStack>
          </WdsButtonGhost>
        </div>
        <div
          v-if="dropdownHasContent"
          class="tw-border-t tw-border-gray-300"
          style="border-top-style: solid"
        ></div>
        <div class="options" :class="dropdownHasContent ? 'tw-py-2' : ''">
          <WdsStack v-show="loading" justify-center class="p-5">
            <ButtonSpinner />
          </WdsStack>
          <WdsAlert v-if="searchError" error>
            {{ searchError }}
          </WdsAlert>
          <div v-if="options.length">
            <div
              v-for="option in options"
              :key="option.value"
              class="option"
              @click="onSelect(option)"
            >
              <slot name="option" :option="option">
                {{ option.label }}
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { WdsStack, WdsInput, WdsButtonGhost, WdsAlert } from '@worksome/wds-components'
import ButtonSpinner from '@/components/ButtonSpinner.vue'

export default {
  components: { ButtonSpinner, WdsInput, WdsButtonGhost, WdsStack, WdsAlert },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    error: {
      type: String,
      default: undefined,
    },
    searchError: {
      type: String,
      default: undefined,
    },
    showCreateButton: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: undefined,
    },
    searchTerm: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    createButtonText: {
      type: String,
      default: $t('Create'),
    },
    modelValue: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      show: false,
    }
  },
  computed: {
    dropdownHasContent() {
      return this.options.length || this.searchError || this.loading
    },
  },
  methods: {
    onSelect(option) {
      this.show = false
      this.$emit('selectOption', option)
    },
    handleInput(value) {
      this.show = !!value
      this.$emit('update:modelValue', value)
    },
    handleClickCreate() {
      this.show = false
      this.$emit('create')
    },
    onClickOutside() {
      this.show = false
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.creatable-select {
  @apply tw-relative;
  @apply tw-w-full;
}

.creatable-select .selected {
  @apply tw-bg-white;
  @apply tw-p-4;
}

.creatable-select .selected.open {
  border-radius: 4px 4px 0px 0px;
}

.creatable-select .selected.open.empty {
  @apply tw-rounded;
}

.creatable-select .options {
  @apply tw-bg-white;
  @apply tw-left-0;
  @apply tw-right-0;
  @apply tw-px-3;
  @apply tw-max-h-52;
  @apply tw-overflow-auto;
  @apply tw-rounded-b;
  z-index: 1;
}

.creatable-select .options .option {
  @apply tw-py-1;
  @apply tw-px-2;
  @apply tw-rounded;
  @apply tw-cursor-pointer;
}

.creatable-select .options .option:hover {
  @apply tw-bg-indigo-50;
}

.select-wrapper {
  @apply tw-rounded;
  @apply tw-absolute;
  @apply tw-w-full;
  @apply tw-shadow;
  z-index: 15;
}

.create-button {
  .zmdi.zmdi-plus {
    @apply tw-text-base;
  }
  @apply tw-w-full;
}
</style>
